// Past drill list — one card per session, newest first. Tapping a card
// opens AptitudeReview for the full Q-by-Q breakdown.

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { DrillHistoryItem, aptitudeDrillAPI } from '../services/aptitudeDrillAPI';
import { track } from '../services/api';
import { displayTopic } from '../config/topicDisplayNames';
import { useTheme } from '../contexts/ThemeContext';

// Theme-aware palette. Light values are byte-for-byte the originals.
const makeColors = (dark: boolean) => ({
  primary: dark ? '#86c232' : '#4a7c10',
  border: dark ? '#262c35' : '#e2e8f0',
  bg: dark ? '#0f1216' : '#f8fafc',
  textPrimary: dark ? '#e6e8eb' : '#0f172a',
  textMuted: dark ? '#aab1ba' : '#64748b',
  done: dark ? '#86c232' : '#16a34a',
  doneBg: dark ? 'rgba(134,194,50,0.12)' : '#dcfce7',
  inProgress: dark ? '#f5c563' : '#f59e0b',
  inProgressBg: dark ? 'rgba(245,176,65,0.12)' : '#fef3c7',
});

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

const AptitudeHistory: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const COLORS = makeColors(dark);
  // Same component is rendered at /aptitude-drill/history AND under
  // /tg/aptitude-drill/history (Telegram Mini App). Match the prefix when
  // navigating internally so we stay inside the right shell.
  const isTg = location.pathname.startsWith('/tg/');
  const basePath = isTg ? '/tg/aptitude-drill' : '/aptitude-drill';
  const [items, setItems] = useState<DrillHistoryItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await aptitudeDrillAPI.history(50);
        if (!cancelled) {
          setItems(data.sessions);
          track('aptitude_history.opened', { count: data.count });
        }
      } catch (e: any) {
        if (e?.response?.status === 401) {
          navigate('/login');
          return;
        }
        if (!cancelled) setError(e?.message || 'Failed to load history');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (error) {
    return (
      <div style={{ padding: 24, color: dark ? '#ff6b6b' : '#dc2626' }}>
        Couldn't load your drill history: {error}
      </div>
    );
  }

  if (items === null) {
    return <div style={{ padding: 24, color: COLORS.textMuted }}>Loading…</div>;
  }

  return (
    <div style={{ padding: 16, maxWidth: 720, margin: '0 auto', minHeight: '100vh', background: COLORS.bg }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'transparent',
            border: 'none',
            fontSize: 20,
            cursor: 'pointer',
            color: COLORS.textPrimary,
          }}
        >
          ←
        </button>
        <h1 style={{ margin: 0, fontSize: 22, color: COLORS.textPrimary }}>
          Past drills
        </h1>
      </div>

      {items.length === 0 ? (
        <div
          style={{
            padding: 32,
            textAlign: 'center',
            color: COLORS.textMuted,
            background: dark ? '#171b21' : '#fff',
            border: `1px solid ${COLORS.border}`,
            borderRadius: 12,
          }}
        >
          You haven't done any drills yet.
          <div style={{ marginTop: 12 }}>
            <button
              onClick={() => navigate(`${basePath}/today`)}
              style={{
                background: COLORS.primary,
                color: dark ? '#0f1216' : '#fff',
                padding: '8px 16px',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              Start today's drill →
            </button>
          </div>
        </div>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 12 }}>
          {items.map((s) => {
            const isDone = s.status === 'completed';
            const statusBg = isDone ? COLORS.doneBg : COLORS.inProgressBg;
            const statusColor = isDone ? COLORS.done : COLORS.inProgress;
            const statusLabel = isDone ? 'Done' : 'In progress';
            return (
              <li
                key={s.session_id}
                onClick={() => {
                  track('aptitude_history.card_tap', { session_id: s.session_id });
                  navigate(`${basePath}/review/${s.session_id}`);
                }}
                style={{
                  background: dark ? '#171b21' : '#fff',
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 12,
                  padding: 16,
                  cursor: 'pointer',
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: 14,
                      color: COLORS.textMuted,
                      marginBottom: 4,
                    }}
                  >
                    {formatDate(s.completed_at || s.started_at)}
                    {s.path_day_index ? `  ·  Day ${s.path_day_index}` : ''}
                  </div>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: COLORS.textPrimary,
                    }}
                  >
                    {s.strategy_title || displayTopic(s.topic || 'Aptitude') || 'Aptitude drill'}
                  </div>
                  <div
                    style={{
                      marginTop: 8,
                      display: 'inline-block',
                      padding: '2px 8px',
                      fontSize: 12,
                      borderRadius: 999,
                      background: statusBg,
                      color: statusColor,
                    }}
                  >
                    {statusLabel}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 24, fontWeight: 700, color: COLORS.textPrimary }}>
                    {s.correct}
                    <span style={{ fontSize: 14, color: COLORS.textMuted }}>/{s.total}</span>
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                    {s.score_pct}%
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default AptitudeHistory;
